/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ADD_LMR_GROUPS_FORM_FIELDS } from "../../constants";
import AuthContext from "../../store/authContext";
import { ILMRGroupData } from "../../types";
import { errorAlert, makeApiRequest, successAlert } from "../../util";
import Form from "../../ui/Form/Form";
import TextField from "../inputs/TextField";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import Button from "../buttons/Button";
import { LMRGroupsAdd } from "../../api/icCheck";
import {
  CREATE_LMR_GROUP_TRANSLATIONS,
  FORM_VALIDATION_TRANSLATIONS,
} from "../../translations/util";
import DropdownField from "../inputs/DropdownField";

interface IProps {
  org: string;
}

const set = new Set();

const isFormInvalid = ({ data }: { data: any }) => {
  if (!data) return true;

  const isConnectionFormInvalid = ADD_LMR_GROUPS_FORM_FIELDS.map(
    (rows: any) => {
      const res = rows.fields.map((field: any) => {
        switch (field.type) {
          case "TEXT":
            return field.isInputInvalid?.(data[field.name]);
          case "DROPDOWN":
            return field.isInputInvalid?.(data[field.name]);
          default:
            return false;
        }
      });
      return res.reduce((acc: any, el: any) => acc || el);
    }
  ).reduce((acc: any, el: any) => acc || el);

  return isConnectionFormInvalid;
};

const NewLMRGroup = ({ org }: IProps) => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const [connectionData, setConnectionData] = useState<ILMRGroupData>({
    "TG-ID": "",
    "LMR-NW-TYPE": undefined,
    "MAPPED-MCPTT-GID": "",
  });

  const createLMRGroup = async () => {
    setLoading(true);
    try {
      await LMRGroupsAdd({
        org,
        token,
        data: JSON.stringify({
          "TG-ID": connectionData["TG-ID"],
          "MAPPED-MCPTT-GID": connectionData["MAPPED-MCPTT-GID"],
        }),
      });

      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/lmr-groups`,
        method: "POST",
        body: JSON.stringify({
          "TG-ID": connectionData["TG-ID"],
          "LMR-NW-TYPE": connectionData["LMR-NW-TYPE"],
          "MAPPED-MCPTT-GID": connectionData["MAPPED-MCPTT-GID"],
        }),
        token,
        org,
      });
      successAlert(CREATE_LMR_GROUP_TRANSLATIONS.successAlertMessage);
      navigate("/configurationManagement/lmrgroups");
    } catch (error: any) {
      errorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    set.clear();
    return () => {
      set.clear();
    };
  }, []);

  return (
    <>
      <div className="data-form">
        <Form>
          <div className="data-tabcontent data-card">
            {ADD_LMR_GROUPS_FORM_FIELDS.map(
              (rows: {
                cols: number;
                fields: {
                  id?: string;
                  name: keyof ILMRGroupData;
                  tooltipText?: string;
                  label: string;
                  invalidText?: string | null;
                  isInputInvalid?: (val?: string) => boolean;
                  required?: boolean;
                  type?: string;
                  items?: [];
                }[];
              }) => {
                return (
                  <div className="grid grid-cols-1 gap-4 form-control single-item">
                    {rows.fields.map((field) => {
                      switch (field.type) {
                        case "DROPDOWN":
                          return (
                            <DropdownField
                              className="dropdown-field"
                              label={field.label}
                              selectedItem={connectionData[field.name]}
                              items={field.items || []}
                              onChange={({
                                selectedItem,
                              }: {
                                selectedItem: string;
                              }) =>
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: selectedItem,
                                })
                              }
                              tooltipText={field.tooltipText}
                              required
                            />
                          );
                        default:
                          return (
                            <TextField
                              key={field?.id}
                              placeholder={field?.label}
                              labelText={field?.label}
                              tooltipText={field?.tooltipText}
                              required={field?.required}
                              value={connectionData[field.name] as string}
                              onChange={(val) => {
                                set.add(field.name);
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: val,
                                });
                              }}
                              invalid={
                                set.has(field?.name) &&
                                field.isInputInvalid?.(
                                  connectionData[field.name] as string
                                )
                              }
                              invalidText={
                                field?.invalidText ??
                                FORM_VALIDATION_TRANSLATIONS.required(
                                  field.label
                                )
                              }
                            />
                          );
                      }
                    })}
                  </div>
                );
              }
            )}
          </div>
        </Form>
      </div>
      <DeleteModal
        modalHeaderText={CREATE_LMR_GROUP_TRANSLATIONS.deleteModal.header}
        modalDescriptionText={
          CREATE_LMR_GROUP_TRANSLATIONS.deleteModal.description
        }
        onDelete={() => navigate("/configurationManagement/lmrgroups")}
        open={open}
        setOpen={setOpen}
        confirmationButtonLabel={
          CREATE_LMR_GROUP_TRANSLATIONS.deleteModal.goBackButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <div className="flex justify-end form-footer">
        <div className="mr-2">
          <Button
            className="w-full"
            type="button"
            kind="tertiary"
            disabled={false}
            label={CREATE_LMR_GROUP_TRANSLATIONS.cancelButtonLabel}
            onClick={() => setOpen(true)}
            dataCy="cancelCreateLMRGroup-"
          />
        </div>

        <div>
          <Button
            className="w-full"
            type="button"
            loading={loading}
            disabled={isFormInvalid({ data: connectionData })}
            onClick={createLMRGroup}
            label={CREATE_LMR_GROUP_TRANSLATIONS.createButtonLabel}
            dataCy="createLMRGroup-"
          />
        </div>
      </div>
    </>
  );
};

export default NewLMRGroup;
